/**
 * Component: SearchFilterSidebar
 * SpecReferenceID: SEARCH-SEMANTIC-V1
 * Requirements: SEARCH-SEMANTIC-V1-R08, SEARCH-SEMANTIC-V1-R09, FRONTEND-WEB-V1-R09, FRONTEND-WEB-V1-R13
 *
 * Capability/facet-driven refine sidebar (CR-006). Options are sourced from the
 * facets the backend returns for the current query; any facet with <=1 distinct
 * value is omitted (so a single-document-type corpus shows no document-type
 * control). Filtering is applied server-side - this component only collects the
 * user's selections.
 *
 * SEARCH-SEMANTIC-V1-R08: Faceted server-side filtering controls.
 * SEARCH-SEMANTIC-V1-R09: Recency prioritisation toggle.
 * FRONTEND-WEB-V1-R09: Expand/collapse filter sections.
 * FRONTEND-WEB-V1-R13: Responsive - sidebar on desktop, sheet on mobile.
 *
 * Accessibility: WCAG 2.1 AA compliant
 */

import React, { useEffect, useMemo, useState } from 'react';
import { SlidersHorizontal, Search as SearchIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ANY_TIME_PRESET_VALUE,
  DATE_PRESETS,
  RECOMMENDATION_FACET_FIELD,
  TYPEAHEAD_FACET_FIELDS,
  hasActiveFilters,
  presetRange,
  type Facet,
  type SearchFilters,
} from './search-filter-types';

export { INITIAL_FILTERS } from './search-filter-types';
export type { SearchFilters, Facet } from './search-filter-types';

interface SearchFilterSidebarProps {
  filters: SearchFilters;
  onFiltersChange: (filters: SearchFilters) => void;
  onReset: () => void;
  facets: Facet[];
  resultCount?: number;
  /** Simple-search only: show the "Limit to figures & tables" toggle (SEARCH-SIMPLE-V1-R07). */
  showFiguresToggle?: boolean;
}

/** A checkbox group for a single low-cardinality facet (e.g. document type). */
function FacetCheckboxGroup({
  facet,
  selected,
  onToggle,
}: {
  facet: Facet;
  selected: string[];
  onToggle: (value: string) => void;
}): React.ReactElement {
  return (
    <div className="space-y-2">
      {facet.values.map((fv) => (
        <div key={fv.value} className="flex items-center gap-2">
          <Checkbox
            id={`facet-${facet.field}-${fv.value}`}
            checked={selected.includes(fv.value)}
            onCheckedChange={() => onToggle(fv.value)}
          />
          <Label
            htmlFor={`facet-${facet.field}-${fv.value}`}
            className="flex flex-1 cursor-pointer justify-between text-xs"
          >
            <span>{fv.value}</span>
            {fv.count > 0 && <span className="text-muted-foreground">{fv.count}</span>}
          </Label>
        </div>
      ))}
    </div>
  );
}

/** A searchable typeahead for a high-cardinality facet (e.g. article/ShortTitle). */
function FacetTypeahead({
  facet,
  selected,
  onToggle,
}: {
  facet: Facet;
  selected: string[];
  onToggle: (value: string) => void;
}): React.ReactElement {
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = q
      ? facet.values.filter((fv) => fv.value.toLowerCase().includes(q))
      : facet.values;
    return base.slice(0, 20);
  }, [facet.values, search]);

  return (
    <div className="space-y-2">
      <div className="relative">
        <SearchIcon
          className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={`Search ${facet.label.toLowerCase()}...`}
          className="h-8 pl-7 text-xs"
          aria-label={`Search ${facet.label}`}
        />
      </div>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selected.map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => onToggle(value)}
              className="rounded bg-primary px-2 py-0.5 text-[10px] text-primary-foreground"
              aria-label={`Remove ${value}`}
            >
              {value} ×
            </button>
          ))}
        </div>
      )}
      <div className="max-h-48 space-y-1 overflow-y-auto">
        {filtered.map((fv) => (
          <div key={fv.value} className="flex items-center gap-2">
            <Checkbox
              id={`facet-${facet.field}-${fv.value}`}
              checked={selected.includes(fv.value)}
              onCheckedChange={() => onToggle(fv.value)}
            />
            <Label
              htmlFor={`facet-${facet.field}-${fv.value}`}
              className="flex flex-1 cursor-pointer justify-between text-xs"
            >
              <span className="truncate">{fv.value}</span>
              {fv.count > 0 && (
                <span className="ml-2 shrink-0 text-muted-foreground">{fv.count}</span>
              )}
            </Label>
          </div>
        ))}
      </div>
    </div>
  );
}

function FilterContent({
  filters,
  onFiltersChange,
  onReset,
  facets,
  resultCount,
  showFiguresToggle,
}: SearchFilterSidebarProps): React.ReactElement {
  const active = hasActiveFilters(filters);

  // Capability-driven: only facets with >1 distinct value are actionable.
  // SEARCH-SEMANTIC-V1-R08 (Bug #5057): a facet that has an active selection
  // stays visible even when it has collapsed to a single value (filtering by it
  // recomputes the facet to that one value), so the user can still deselect it.
  const visibleFacets = useMemo(
    () =>
      facets.filter(
        (f) =>
          f.values.length > 1 ||
          (filters.facetSelections[f.field]?.length ?? 0) > 0,
      ),
    [facets, filters.facetSelections],
  );

  // SEARCH-SEMANTIC-V1-R08 (Bug #5051): the "Recommendations only" toggle is
  // capability-gated - shown only when the corpus actually has recommendations
  // (the RecommendationStrength facet returned values), or while it is already
  // active so the user can still turn it off.
  const hasRecommendations = useMemo(
    () =>
      filters.recommendationsOnly ||
      facets.some(
        (f) => f.field === RECOMMENDATION_FACET_FIELD && f.values.length > 0,
      ),
    [facets, filters.recommendationsOnly],
  );

  /**
   * Toggle a facet value, applying the COR-class coupling rule:
   * selecting any RecommendationStrength value implies "Recommendations only".
   */
  const toggleFacetValue = (field: string, value: string): void => {
    const current = filters.facetSelections[field] ?? [];
    const nextValues = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    const nextSelections = { ...filters.facetSelections, [field]: nextValues };
    if (nextValues.length === 0) delete nextSelections[field];

    let recommendationsOnly = filters.recommendationsOnly;
    if (field === RECOMMENDATION_FACET_FIELD) {
      recommendationsOnly = nextValues.length > 0 ? true : recommendationsOnly;
    }
    onFiltersChange({
      ...filters,
      facetSelections: nextSelections,
      recommendationsOnly,
    });
  };

  /** Toggling "Recommendations only" off clears any selected COR classes. */
  const toggleRecommendationsOnly = (checked: boolean): void => {
    const nextSelections = { ...filters.facetSelections };
    if (!checked) delete nextSelections[RECOMMENDATION_FACET_FIELD];
    onFiltersChange({
      ...filters,
      recommendationsOnly: checked,
      facetSelections: nextSelections,
    });
  };

  // SEARCH-SEMANTIC-V1-R08 (Bug #5055): buffer the date inputs locally and only
  // commit (which re-runs the search) on blur, so navigating the native date
  // picker does not fire the search with an intermediate value. Local state is
  // kept in sync when the dates change externally (preset, Clear all).
  const [dateFromInput, setDateFromInput] = useState(filters.dateFrom);
  const [dateToInput, setDateToInput] = useState(filters.dateTo);
  useEffect(() => setDateFromInput(filters.dateFrom), [filters.dateFrom]);
  useEffect(() => setDateToInput(filters.dateTo), [filters.dateTo]);
  const commitDate = (key: 'dateFrom' | 'dateTo', value: string): void => {
    if (value !== filters[key]) onFiltersChange({ ...filters, [key]: value });
  };

  // SEARCH-SEMANTIC-V1-R08 (Bug #5054): a preset selection fills BOTH the From
  // and To fields (From = relative date, To = today) so the range is visible and
  // editable; "Any time" clears the range. Manual custom ranges use the inputs.
  const applyPreset = (value: string): void => {
    const range = presetRange(value);
    if (range) {
      onFiltersChange({ ...filters, dateFrom: range.from, dateTo: range.to });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Refine your search</h3>
        {active && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onReset}
            className="h-auto px-2 py-1 text-xs"
          >
            Clear all
          </Button>
        )}
      </div>

      {resultCount !== undefined && (
        <p className="text-xs text-muted-foreground">
          {resultCount} result{resultCount !== 1 ? 's' : ''} shown
        </p>
      )}

      {/* Quick filters (SEARCH-SEMANTIC-V1-R08 / R09) */}
      <div className="space-y-3">
        {hasRecommendations && (
          <div className="flex items-center gap-2">
            <Checkbox
              id="filter-recommendations"
              checked={filters.recommendationsOnly}
              onCheckedChange={(checked) =>
                toggleRecommendationsOnly(checked === true)
              }
            />
            <Label htmlFor="filter-recommendations" className="cursor-pointer text-sm">
              Recommendations only
            </Label>
          </div>
        )}
        <div className="flex items-center gap-2">
          <Checkbox
            id="filter-recent"
            checked={filters.prioritizeRecent}
            onCheckedChange={(checked) =>
              onFiltersChange({ ...filters, prioritizeRecent: checked === true })
            }
          />
          <Label htmlFor="filter-recent" className="cursor-pointer text-sm">
            Prioritize recent publications
          </Label>
        </div>
        {showFiguresToggle && (
          <div className="flex items-center gap-2">
            <Checkbox
              id="filter-figures-tables"
              checked={filters.limitToFiguresAndTables}
              onCheckedChange={(checked) =>
                onFiltersChange({
                  ...filters,
                  limitToFiguresAndTables: checked === true,
                })
              }
            />
            <Label htmlFor="filter-figures-tables" className="cursor-pointer text-sm">
              Limit to figures &amp; tables
            </Label>
          </div>
        )}
      </div>

      <Accordion type="multiple" defaultValue={['date-range']}>
        {/* Date Range with presets (SEARCH-SEMANTIC-V1-R08) */}
        <AccordionItem value="date-range">
          <AccordionTrigger className="py-2 text-sm">Publication Date</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3">
              <Select value="" onValueChange={applyPreset}>
                <SelectTrigger className="h-8 text-xs" aria-label="Quick date range">
                  <SelectValue placeholder="Quick range..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ANY_TIME_PRESET_VALUE}>Any time</SelectItem>
                  {DATE_PRESETS.map((preset) => (
                    <SelectItem key={preset.label} value={preset.label}>
                      {preset.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="space-y-2">
                <div>
                  <Label htmlFor="date-from" className="text-xs">
                    From
                  </Label>
                  <Input
                    id="date-from"
                    type="date"
                    value={dateFromInput}
                    onChange={(e) => setDateFromInput(e.target.value)}
                    onBlur={() => commitDate('dateFrom', dateFromInput)}
                    className="block h-8 w-full text-sm"
                  />
                </div>
                <div>
                  <Label htmlFor="date-to" className="text-xs">
                    To
                  </Label>
                  <Input
                    id="date-to"
                    type="date"
                    value={dateToInput}
                    onChange={(e) => setDateToInput(e.target.value)}
                    onBlur={() => commitDate('dateTo', dateToInput)}
                    className="block h-8 w-full text-sm"
                  />
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Facet-driven sections - rendered only when the corpus supports them */}
        {visibleFacets.map((facet) => {
          const selected = filters.facetSelections[facet.field] ?? [];
          const isTypeahead = TYPEAHEAD_FACET_FIELDS.includes(facet.field);
          return (
            <AccordionItem key={facet.field} value={facet.field}>
              <AccordionTrigger className="py-2 text-sm">{facet.label}</AccordionTrigger>
              <AccordionContent>
                {isTypeahead ? (
                  <FacetTypeahead
                    facet={facet}
                    selected={selected}
                    onToggle={(value) => toggleFacetValue(facet.field, value)}
                  />
                ) : (
                  <FacetCheckboxGroup
                    facet={facet}
                    selected={selected}
                    onToggle={(value) => toggleFacetValue(facet.field, value)}
                  />
                )}
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </div>
  );
}

/**
 * SearchFilterSidebar - FRONTEND-WEB-V1-R13: Responsive filter sidebar.
 */
export function SearchFilterSidebar(
  props: SearchFilterSidebarProps,
): React.ReactElement {
  return <FilterContent {...props} />;
}

/**
 * Mobile sheet button that opens the filter sidebar in a slide-over panel.
 * FRONTEND-WEB-V1-R13: Touch-friendly mobile filter access.
 */
export function SearchFilterMobileButton(
  props: SearchFilterSidebarProps,
): React.ReactElement {
  const active = hasActiveFilters(props.filters);
  const activeCount =
    (props.filters.recommendationsOnly ? 1 : 0) +
    (props.filters.prioritizeRecent ? 1 : 0) +
    (props.filters.dateFrom !== '' ? 1 : 0) +
    (props.filters.dateTo !== '' ? 1 : 0) +
    Object.values(props.filters.facetSelections).filter((v) => v.length > 0).length;

  return (
    <div className="mb-4">
      <Sheet>
        <SheetTrigger asChild>
          <Button variant="outline" size="sm" className="gap-2">
            <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />
            Filters
            {active && (
              <span className="ml-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[10px] text-primary-foreground">
                {activeCount}
              </span>
            )}
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="w-72 overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Search Filters</SheetTitle>
          </SheetHeader>
          <div className="mt-4">
            <FilterContent {...props} />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
